import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { debounce, compact, orderBy } from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import { Table } from '@cordata/buffet';
import { toast } from 'react-toastify';
import querystring from 'querystring';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { formatDate } from '~/utils';

import { TableCellDateTime, TableFilterDateTime, TableCellDate, TableFilterDate, TableCellPhone } from '~/components';

import {
  authorizedPyInstance as py,
  authorizedInstance as rb,
  fullAuthorizedInstance as full,
} from '~/utils/api/instances';

const requestData = ({
  pageSize, page = 0, sorted = [], filtered = [],
}, {
  url,
  params,
  instance: i,
}) => {
  const isPython = i === undefined || 'python'.startsWith(i);
  const isRuby = 'ruby'.startsWith(i);
  const isFull = 'full'.startsWith(i);

  let instance;

  if (isRuby) instance = rb;
  if (isPython) instance = py;
  if (isFull) instance = full;

  let modifiedUrl;

  if (isRuby || isFull) modifiedUrl = url;
  if (isPython) modifiedUrl = `api/${url}`;

  const sort =
    sorted.map(s =>
      s.id.split(',')
        .map(attr => `${s.desc ? '-' : ''}${attr.split('__')[0]}`)
        .join(',')).join(',');

  return instance
    .get(modifiedUrl, {
      params: {
        ...params,
        ...isPython && {
          'page-size': pageSize,
          page: page + 1,
          sort,
        },
        ...filtered.filter(f => f.value !== null && f.value !== undefined).reduce((a, m) => ({
          ...a,
          [m.id]: m.value instanceof Date ? formatDate(m.value) : m.value,
        }), {}),
      },
      // need to use querystring.stringify to correctly serialize array params
      paramsSerializer: p => querystring.stringify(p),
    })
    .then(r => r.data)
    .then(r => {
      if (isRuby && sorted.length > 0) {
        /* eslint-disable-next-line */
        r.data = orderBy(r.data, sorted.map(s => s.id), sorted.map(s => s.desc));
      }

      return r;
    });
};

const StandardInput = ({ d, c }) => (
  <input
    {...d}
    {...c.cellType}
    type={c.cellType.type}
    /* eslint-disable-next-line react/no-unknown-property */
    bsSize="sm"
    onChange={e => d.onChange(e.target.value)}
    className="w-100"
    value={d.filter?.value}
  />
);

StandardInput.propTypes = {
  d: PropTypes.shape().isRequired,
  c: PropTypes.shape().isRequired,
};

const UncontrolledTable = ({
  endpoint,
  dataTransform,
  onClick,
  columns: cols,
  detailLevel,
  defaultPageSize,
  defaultSorted,
  defaultExpanded,
  expandedAll,
  name,
  ...props
}) => {
  /*
   *  Available detail levels. to be used with 'omit' key in column definition
   *
   * |==========================|===================================|
   * | col.omitLevel === ____   | omit col when detailType is ____  |
   * |==========================|===================================|
   * |        undefined         | never (always show)               |
   * |--------------------------|-----------------------------------|
   * |        'reduced'         | 'reduced'                         |
   * |--------------------------|-----------------------------------|
   * |        'minimal'         | 'reduced' or 'minimal'            |
   * |==========================|===================================|
   *
   */

  if (typeof endpoint === 'string') {
    /* eslint-disable-next-line no-param-reassign */
    endpoint = { url: endpoint };
  }

  const [hasMultiplePages, setHasMultiplePages] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState();
  const [sorted, setSorted] = useState(localStorage.getItem(`${name}-table-state-sorted`) ? JSON.parse(localStorage.getItem(`${name}-table-state-sorted`)) : defaultSorted);
  const [pageSize, setPageSize] = useState(Number(localStorage.getItem(`${name}-table-state-page-size`)) || defaultPageSize);
  const [expanded, setExpanded] = useState(expandedAll
    ? new Array(pageSize).fill(0)
      .reduce((a, m, i) => ({
        ...a,
        [i]: true,
      }), defaultExpanded)
    : {});
  const showAllData = parseInt(endpoint.params['all-data'], 10) === 1;

  useEffect(() => {
    if (!localStorage.getItem(`${name}-table-state-page-size`)) {
      localStorage.setItem(`${name}-table-state-page-size`, defaultPageSize);
    }

    if (!localStorage.getItem(`${name}-table-state-sorted`)) {
      localStorage.setItem(`${name}-table-state-sorted`, JSON.stringify(defaultSorted));
    }

    if (!showAllData) {
      requestData({ pageSize: 1 }, endpoint)
        .then(r => {
          setHasMultiplePages(r.total > defaultPageSize);
        });
    }
  }, []);

  const columns = cols
    .filter(c => {
      let shouldKeep = true;
      switch (detailLevel) {
        case 'minimal':
          shouldKeep = !['reduced', 'minimal'].includes(c.omitLevel);
          break;
        case 'reduced':
          shouldKeep = c.omitLevel !== 'reduced';
          break;
        default:
          break;
      }
      if (c.omit) {
        shouldKeep = !c.omit();
      }
      return shouldKeep;
    })
    .map((c, idx) => {
      let custom = {
        // make text in columns wrap by default
        Cell: d => (
          <div className="text-white-space-normal">
            {d.value}
          </div>
        ),
        Aggregated: row => (
          row.isExpanded
            ? null
            : (
              <div className="text-white-space-normal">
                {row.value}
              </div>
            )
        ),
      };

      const superCustom = {};

      if (c.headerTooltip) {
        const headerKey = `header-${idx}`;

        superCustom.Header = () => (
          <React.Fragment>
            {c.Header}

            <FontAwesomeIcon icon="exclamation-circle" id={headerKey} className="text-muted ml-2" />

            <UncontrolledTooltip target={headerKey} placement="right">{c.headerTooltip}</UncontrolledTooltip>
          </React.Fragment>
        );
      }

      if (c.cellType) {
        if (typeof c.cellType === 'string') {
          /* eslint-disable-next-line no-param-reassign */
          c.cellType = { type: c.cellType };
        }

        switch (c.cellType.type) {
          case 'primary-indicator':
            custom = {
              ...custom,
              Cell: d => (
                <div className="text-center">{d.value ? <FontAwesomeIcon icon="star" style={{ color: 'orange' }} /> : null}</div>
              ),
              width: 50,
            };

            break;

          case 'choice':
            custom = {
              ...custom,
              Cell: d => (
                <div className="text-center"><FontAwesomeIcon icon={d.value ? 'check' : 'times'} className={d.value ? 'text-success' : 'text-danger'} /></div>
              ),
            };

            break;

          case 'link':
            custom = {
              ...custom,
              Cell: d => (
                <a href={d.value}>
                  Download
                </a>
              ),
            };

            break;

          case 'truncate':
            custom = {
              ...custom,
              Cell: undefined, // react table adds overflow: ellipses by default
            };

            break;

          case 'email':
            custom = {
              ...custom,
              Cell: d => (
                <a href={`mailto:${d.value}`}>
                  {d.value}
                </a>
              ),
              Filter: d =>
                <StandardInput c={c} d={d} />,
            };

            break;

          case 'number':
          case 'url':
            custom = {
              ...custom,
              Filter: d =>
                <StandardInput c={c} d={d} />,
            };

            break;

          case 'date':
            custom = {
              ...custom,
              Cell: d => (
                <TableCellDate
                  {...d}
                  {...c.cellType}
                />
              ),
              Filter: d => (
                <TableFilterDate
                  {...d}
                  {...c.cellType}
                />
              ),
            };

            break;

          case 'tel':
            custom = {
              ...custom,
              Cell: d => (
                <TableCellPhone
                  {...d}
                  {...c.cellType}
                />
              ),
              Filter: d =>
                <StandardInput c={c} d={d} />,
            };

            break;

          case 'date-time':
          case 'datetime':
            custom = {
              ...custom,
              Cell: d => (
                <TableCellDateTime
                  {...d}
                  {...c.cellType}
                />
              ),
              Filter: d => (
                <TableFilterDateTime
                  {...d}
                  {...c.cellType}
                />
              ),
            };

            break;

          default:
            break;
        }
      }

      return {
        ...custom,
        ...c,
        ...superCustom,
      };
    });

  const fetchData = (state) => {
    setLoading(true);
    setData([]);

    localStorage.setItem(`${name}-table-state-page-size`, state.pageSize);
    setPageSize(state.pageSize);
    localStorage.setItem(`${name}-table-state-sorted`, JSON.stringify(state.sorted));
    setSorted(state.sorted);

    return requestData({ sorted: defaultSorted, ...state }, endpoint)
      .then((r) => {
        setData(compact(dataTransform(r.data)));
        setPages(Math.ceil(r.total / (state.pageSize || defaultPageSize)));

        if (showAllData) {
          setPages(1);
          setHasMultiplePages(false);
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error('Oops, something went wrong!');
      })
      .finally(() => setLoading(false));
  };

  /* eslint-disable-next-line */
  const trProps = (state, rowInfo, column, instance) => {
    return {
      onClick: (e, handleOriginal) => {
        if (handleOriginal) {
          handleOriginal();
        } else {
          onClick(rowInfo?.original);
        }
      },
      ...(!!onClick && {
        style: {
          cursor: 'pointer',
        },
      }),
    };
  };

  const debouncedFetch = useMemo(() => debounce(
    fetchData,
    750,
  ), [endpoint]);

  useEffect(() => {
    if (showAllData) {
      requestData({ pageSize: 1, sorted }, endpoint)
        .then((d) => {
          setData(d.data);
          setPageSize(d.total);
          setPages(1);
          setHasMultiplePages(false);
        });
    }
  }, [showAllData]);

  return (
    <Table
      minRows={data.length === 0 ? 2 : 0}
      columns={columns}
      data={data}
      pages={pages}
      loading={loading}
      manual
      defaultPageSize={pageSize}
      defaultSorted={sorted}
      expanded={expanded}
      onExpandedChange={(newExpanded, index) => {
        setExpanded(p => {
          const itemIndex = index[0];
          const isExpanded = p[itemIndex];
          const expandedList = { ...expanded };

          expandedList[itemIndex] = !isExpanded;

          return expandedList;
        });
      }}
      onFetchData={debouncedFetch}
      noDataText={loading ? 'Loading' : 'No rows found'}
      getTrProps={trProps}
      filterable={hasMultiplePages}
      showPaginationBottom={hasMultiplePages}
      {...props}
    />
  );
};

UncontrolledTable.propTypes = {
  endpoint: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(),
  ]).isRequired,
  dataTransform: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  detailLevel: PropTypes.string,
  defaultSorted: PropTypes.arrayOf(PropTypes.shape()),
  onClick: PropTypes.func,
  defaultPageSize: PropTypes.number,
  name: PropTypes.string.isRequired,
  expandedAll: PropTypes.bool,
  defaultExpanded: PropTypes.shape(),
};

UncontrolledTable.defaultProps = {
  dataTransform: data => data,
  detailLevel: undefined,
  defaultSorted: [],
  onClick: () => {},
  defaultPageSize: 10,
  expandedAll: false,
  defaultExpanded: {},
};

export default UncontrolledTable;
